import React, { FC, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { ThemeProvider } from 'styled-components';

import { BaseStyle, CoreUtils, ShortCodeEnums } from '@nhl/nhl-breakaway-ui';
import TranslationLoader from '@nhl/nhl-react-ui/lib/TranslationLoader';
import { useGamePolling } from '@nhl/react-hooks/lib/useGamePolling';

import CommonMessages from '~/core/commonMessages';
import { ThemeName } from '~/core/enums';
import { Game } from '~/core/types';
import { defaultLocationData, useLocationQuery } from '~/redux/ducks/location';
import configureStore from '~/redux/store';

import BaseCarousel, { Props as BaseCarouselProps } from '../baseCarousel/BaseCarousel';
import CarouselContextProvider, { CarouselContext } from '../baseCarousel/CarouselContextProvider';
import { SlideType } from '../baseCarousel/enums';
import { SlideData, SlideMetadata } from '../baseCarousel/types';
import useFetchConfig from '../hooks/useFetchConfig';

import SeriesSlide from './components/SeriesSlide';
import { PlayoffResponse, PlayoffSeries } from './types';

import {
    FilledChevronLeft,
    FilledChevronRight,
    NextNavControl,
    PrevNavControl,
    SERIES_SKINNY_SLIDE_WIDTH,
    SERIES_SLIDE_HEIGHT,
    SERIES_SLIDE_WIDTH,
    SeriesPickerWrapper,
    SeriesRound,
    SeriesSkinnySlide,
    SkinnySlideLeft,
    SkinnySlideRight,
} from './styles';

type Props = {
    games?: Array<Game>;
    language: ShortCodeEnums.Language;
    season: string;
    theme?: ThemeName;
    baseCarouselProps?: Partial<BaseCarouselProps<PlayoffSeries>>;
};

const SeriesPickerComponent: FC<Props> = ({
    games = [],
    language,
    season,
    theme = ThemeName.dark,
    baseCarouselProps,
}) => {
    const resolvedTheme = theme === ThemeName.dark ? BaseStyle.DarkTheme : BaseStyle.LightTheme;

    const [playoffResponse, setPlayoffResponse] = useState<PlayoffResponse>();

    const carouselContext = useContext(CarouselContext);

    const config = useFetchConfig();

    const { data: countryCode = defaultLocationData } = useLocationQuery(
        {
            baseUrl: config?.webAPIBaseURL,
        },
        { skip: config === undefined }
    );

    const fetchCarouselData = useCallback(async () => {
        if (config !== undefined) {
            const response = await fetch(`${config.webAPIBaseURL}/playoff-series/carousel/${season}/`, {
                credentials: 'include',
            });
            const playoffs = await response.json();

            setPlayoffResponse(playoffs);
        }
    }, [config, season]);

    useEffect(() => {
        fetchCarouselData();
    }, [fetchCarouselData]);

    useGamePolling(fetchCarouselData, games);

    // whenever we get new series data we loop through the responses. For each new round we add a round slide
    // and then add all the series for that date. In addition, we also have an array of booleans to indicate
    // which slides are round names (the indices will match up with the indices in the slides array). This will
    // help us in the visibleSlides computation below.
    const slideData = useMemo((): SlideData<PlayoffSeries> => {
        const slides: Array<ReactNode> = [];
        const slideMetadata: Array<SlideMetadata<PlayoffSeries>> = [];

        const reversedRounds = playoffResponse?.rounds?.slice().reverse();

        reversedRounds?.forEach((round) => {
            slides.push(
                <SeriesSkinnySlide key={round.roundNumber}>
                    {CommonMessages.playoffRoundDescriptions.medium[round.roundAbbrev] && (
                        <SeriesRound>
                            <CoreUtils.SafeFormattedMessage
                                message={CommonMessages.playoffRoundDescriptions.medium[round.roundAbbrev]}
                            />
                        </SeriesRound>
                    )}
                </SeriesSkinnySlide>
            );

            slideMetadata.push({ key: round.roundNumber, type: SlideType.PLAYOFF_ROUND });

            round.series.forEach((series) => {
                slides.push(
                    <SeriesSlide
                        key={`${series.bottomSeed.abbrev} x ${series.topSeed.abbrev}`}
                        series={series}
                        config={config}
                        broadcastCountryCode={countryCode}
                        theme={resolvedTheme}
                        language={language}
                    />
                );

                slideMetadata.push({
                    key: `${series.bottomSeed.abbrev} x ${series.topSeed.abbrev}`,
                    originalData: series,
                    type: SlideType.SERIES,
                });
            });
        });

        return {
            slides,
            slideMetadata,
        };
    }, [config, countryCode, language, resolvedTheme, playoffResponse]);

    const loadTranslations = async (language: string) => {
        if (config !== undefined) {
            const response = await fetch(`${config.translationsBaseURL}/${language}.json?rel=${config.appId}`);
            return await response.json();
        }
    };

    if (config === undefined) {
        return null;
    }

    return (
        <TranslationLoader callback={loadTranslations} activeLanguage={language} showLoader={false}>
            <ThemeProvider theme={resolvedTheme}>
                <BaseStyle.RootStyle>
                    <SeriesPickerWrapper>
                        <BaseCarousel<PlayoffSeries>
                            height={SERIES_SLIDE_HEIGHT}
                            regularSlideWidth={SERIES_SLIDE_WIDTH}
                            renderNextButton={(slideNext) =>
                                carouselContext.shouldShowControls &&
                                slideData.slides.length >
                                    carouselContext.activeIndex + carouselContext.visibleSlides && (
                                    <SkinnySlideRight>
                                        <NextNavControl onClick={slideNext}>
                                            <FilledChevronRight />
                                        </NextNavControl>
                                    </SkinnySlideRight>
                                )
                            }
                            renderPrevButton={(slidePrev) =>
                                carouselContext.shouldShowControls &&
                                carouselContext.activeIndex !== 0 && (
                                    <SkinnySlideLeft>
                                        <PrevNavControl onClick={slidePrev}>
                                            <FilledChevronLeft />
                                        </PrevNavControl>
                                    </SkinnySlideLeft>
                                )
                            }
                            shouldExpandBelowMediumScreens
                            slideData={slideData}
                            skinnySlideWidth={SERIES_SKINNY_SLIDE_WIDTH}
                            {...baseCarouselProps}
                        />
                    </SeriesPickerWrapper>
                </BaseStyle.RootStyle>
            </ThemeProvider>
        </TranslationLoader>
    );
};

const WrappedSeriesPicker: FC<Props> = (props) => (
    <CarouselContextProvider>
        <SeriesPickerComponent {...props} />
    </CarouselContextProvider>
);

export default WrappedSeriesPicker;

type SeriesPickerArgs = {
    rootElementId: string;
    season: string;
    language: ShortCodeEnums.Language;
    theme?: ThemeName;
};

export const SeriesPicker = async ({
    rootElementId,
    season,
    language = ShortCodeEnums.Language.English,
    theme = ThemeName.dark,
}: SeriesPickerArgs) => {
    const rootElement = document.getElementById(rootElementId);

    if (rootElement !== null) {
        const root = createRoot(rootElement);
        const store = configureStore();

        root.render(
            <Provider store={store}>
                <WrappedSeriesPicker season={season} language={language} theme={theme} />
            </Provider>
        );
    }
};
